import { useSyncExternalStore } from 'react';

/**
 * Apparence : thème (automatique, sombre, clair) et fond d'écran (tableau,
 * discret, aucun). Choix enregistré dans le navigateur. Dans l'intranet,
 * l'adresse (?theme=…&wallpaper=…) ou un message de la page parente
 * ({ type: 'pujolisique:theme', theme, wallpaper }) l'imposent pour l'onglet.
 * public/theme.js applique le même calcul avant le premier affichage.
 */
export type ThemeChoice = 'auto' | 'dark' | 'light';
export type Wallpaper = 'painting' | 'soft' | 'none';
export interface Appearance {
  theme: ThemeChoice;
  wallpaper: Wallpaper;
  /** Thème effectivement affiché */
  resolved: 'dark' | 'light';
  /** Imposé par l'intranet pour cet onglet */
  embedded: boolean;
}

const KEY = 'pjs-appearance';
const EMBED_KEY = 'pjs-appearance-embed';
const THEMES: ThemeChoice[] = ['auto', 'dark', 'light'];
const WALLS: Wallpaper[] = ['painting', 'soft', 'none'];
const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: light)') : null;

function readJson(storage: Storage | undefined, key: string): Partial<Appearance> {
  try {
    return JSON.parse(storage?.getItem(key) || '{}');
  } catch {
    return {};
  }
}
function valid<T extends string>(v: unknown, list: T[]): T | undefined {
  return list.includes(v as T) ? (v as T) : undefined;
}

function compute(): Appearance {
  const saved = readJson(globalThis.localStorage, KEY);
  const embed = readJson(globalThis.sessionStorage, EMBED_KEY);
  const theme = valid(embed.theme, THEMES) ?? valid(saved.theme, THEMES) ?? 'auto';
  const wallpaper = valid(embed.wallpaper, WALLS) ?? valid(saved.wallpaper, WALLS) ?? 'painting';
  const resolved = theme === 'auto' ? (mq?.matches ? 'light' : 'dark') : theme;
  return { theme, wallpaper, resolved, embedded: Boolean(embed.theme || embed.wallpaper) };
}

let state = compute();
const listeners = new Set<() => void>();

function apply() {
  state = compute();
  const r = document.documentElement;
  r.dataset.theme = state.resolved;
  r.dataset.wallpaper = state.wallpaper;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', state.resolved === 'light' ? '#f6f1e4' : '#0d1611');
  listeners.forEach((l) => l());
}

function setEmbedded(v: Partial<Pick<Appearance, 'theme' | 'wallpaper'>>) {
  const cur = readJson(globalThis.sessionStorage, EMBED_KEY);
  const next = {
    theme: valid(v.theme, THEMES) ?? cur.theme,
    wallpaper: valid(v.wallpaper, WALLS) ?? cur.wallpaper,
  };
  try {
    sessionStorage.setItem(EMBED_KEY, JSON.stringify(next));
  } catch {
    /* stockage indisponible : le thème s'applique quand même pour la page */
  }
  apply();
}

/** Choix de la personne (Réglages) : enregistré, et l'emporte sur l'intranet */
export function setAppearance(v: Partial<Pick<Appearance, 'theme' | 'wallpaper'>>) {
  const next = { theme: state.theme, wallpaper: state.wallpaper, ...v };
  try {
    localStorage.setItem(KEY, JSON.stringify({ theme: next.theme, wallpaper: next.wallpaper }));
    sessionStorage.removeItem(EMBED_KEY);
  } catch {
    /* navigation privée : le choix vaut pour la page */
  }
  apply();
}

export function initAppearance() {
  const q = new URLSearchParams(location.search);
  if (q.has('theme') || q.has('wallpaper')) {
    setEmbedded({ theme: q.get('theme') as ThemeChoice, wallpaper: q.get('wallpaper') as Wallpaper });
  } else apply();
  mq?.addEventListener('change', () => state.theme === 'auto' && apply());
  window.addEventListener('storage', (e) => e.key === KEY && apply());
  window.addEventListener('message', (e) => {
    if (e.source === window.parent && e.source !== window && e.data?.type === 'pujolisique:theme') setEmbedded(e.data);
  });
}

export function useAppearance(): Appearance {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}
